import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
    deleteToken,
    getMessaging,
    getToken,
    isSupported,
    onMessage,
} from 'firebase/messaging';
import {
    FIREBASE_API_KEY,
    FIREBASE_APP_ID,
    FIREBASE_AUTH_DOMAIN,
    FIREBASE_MESSAGING_SENDER_ID,
    FIREBASE_PROJECT_ID,
    FIREBASE_STORAGE_BUCKET,
    FIREBASE_VAPID_KEY,
} from 'src/config';

const messagingConfig = {
    apiKey: FIREBASE_API_KEY,
    authDomain: FIREBASE_AUTH_DOMAIN,
    projectId: FIREBASE_PROJECT_ID,
    storageBucket: FIREBASE_STORAGE_BUCKET,
    messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
    appId: FIREBASE_APP_ID,
};

const hasMessagingConfig = () => {
    return Boolean(
        messagingConfig.apiKey &&
            messagingConfig.authDomain &&
            messagingConfig.projectId &&
            messagingConfig.storageBucket &&
            messagingConfig.messagingSenderId &&
            messagingConfig.appId &&
            FIREBASE_VAPID_KEY,
    );
};

const getFirebaseApp = (): FirebaseApp | null => {
    if (!hasMessagingConfig()) {
        return null;
    }

    if (getApps().length) {
        return getApp();
    }

    return initializeApp(messagingConfig);
};

const getFirebaseMessagingServiceWorkerUrl = () => {
    return '/firebase-messaging-sw.js';
};

const waitForServiceWorkerActivation = async (
    registration: ServiceWorkerRegistration,
): Promise<ServiceWorkerRegistration> => {
    if (registration.active?.state === 'activated') {
        return registration;
    }

    const waitingWorker = registration.waiting;
    if (waitingWorker) {
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }

    const pendingWorker = registration.installing || registration.waiting;

    if (pendingWorker) {
        await new Promise<void>(resolve => {
            const handleStateChange = () => {
                if (pendingWorker.state === 'activated') {
                    pendingWorker.removeEventListener(
                        'statechange',
                        handleStateChange,
                    );
                    resolve();
                }
            };

            pendingWorker.addEventListener('statechange', handleStateChange);
            handleStateChange();
        });
    }

    await navigator.serviceWorker.ready;

    return registration;
};

const clearFirebaseMessagingServiceWorkers = async (): Promise<void> => {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const workerUrl = getFirebaseMessagingServiceWorkerUrl();

    await Promise.all(
        registrations
            .filter(registration => {
                const scriptUrls = [
                    registration.active?.scriptURL,
                    registration.waiting?.scriptURL,
                    registration.installing?.scriptURL,
                ].filter(Boolean) as string[];

                return scriptUrls.some(scriptUrl => {
                    if (!scriptUrl.includes('/firebase-messaging-sw.js')) {
                        return false;
                    }

                    const resolvedExpected = new URL(
                        workerUrl,
                        window.location.origin,
                    ).href;

                    return scriptUrl !== resolvedExpected;
                });
            })
            .map(registration => registration.unregister()),
    );
};

const isStorageRegistrationError = (error: unknown) => {
    if (!(error instanceof Error)) {
        return false;
    }

    return (
        error.name === 'AbortError' &&
        error.message.toLowerCase().includes('storage error')
    );
};

const registerMessagingServiceWorker = async (
    retry = true,
): Promise<ServiceWorkerRegistration> => {
    try {
        await clearFirebaseMessagingServiceWorkers();

        return await navigator.serviceWorker.register(
            getFirebaseMessagingServiceWorkerUrl(),
        );
    } catch (error) {
        if (!retry || !isStorageRegistrationError(error)) {
            throw error;
        }

        await clearFirebaseMessagingServiceWorkers();

        return navigator.serviceWorker.register(
            getFirebaseMessagingServiceWorkerUrl(),
        );
    }
};

const buildMessagesTargetPath = (messageId?: string) => {
    if (!messageId) {
        return '/app/messages';
    }

    return `/app/messages?messageId=${encodeURIComponent(messageId)}`;
};

const buildWebPushTargetPath = (
    data: Record<string, string | undefined>,
): string => {
    if (data.targetPath?.startsWith('/app/')) {
        return data.targetPath;
    }

    if (data.type === 'event' || data.type === 'event_reminder') {
        return '/app/calendar';
    }

    return buildMessagesTargetPath(data.messageId);
};

export type ForegroundWebPushPayload = Record<string, string | undefined>;

type ForegroundWebPushListener = (payload: ForegroundWebPushPayload) => void;

const foregroundWebPushListeners = new Set<ForegroundWebPushListener>();

const notifyForegroundWebPushListeners = (
    payload: ForegroundWebPushPayload,
) => {
    foregroundWebPushListeners.forEach(listener => {
        try {
            listener(payload);
        } catch (error) {
            console.warn('Foreground web push listener failed', error);
        }
    });
};

let stopForegroundListener: (() => void) | null = null;

export const subscribeToForegroundWebPushMessages = (
    listener: ForegroundWebPushListener,
) => {
    foregroundWebPushListeners.add(listener);

    return () => {
        foregroundWebPushListeners.delete(listener);
    };
};

export const registerWebPushToken = async ({
    requestPermission = true,
    onPermissionChange,
}: {
    requestPermission?: boolean;
    onPermissionChange?: (permission: NotificationPermission) => void;
} = {}): Promise<string | null> => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return null;
    }

    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        return null;
    }

    if (!hasMessagingConfig()) {
        return null;
    }

    const supported = await isSupported().catch(() => false);
    if (!supported) {
        return null;
    }

    const permission =
        Notification.permission === 'default' && requestPermission
            ? await Notification.requestPermission()
            : Notification.permission;

    onPermissionChange?.(permission);

    if (permission !== 'granted') {
        return null;
    }

    const app = getFirebaseApp();
    if (!app || !FIREBASE_VAPID_KEY) {
        return null;
    }

    const registration = await registerMessagingServiceWorker();
    await waitForServiceWorkerActivation(registration);

    const messaging = getMessaging(app);

    return getToken(messaging, {
        vapidKey: FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
    });
};

export const unregisterWebPushToken = async (): Promise<void> => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return;
    }

    if (!hasMessagingConfig()) {
        return;
    }

    const supported = await isSupported().catch(() => false);
    if (!supported) {
        return;
    }

    const app = getFirebaseApp();
    if (!app) {
        return;
    }

    const messaging = getMessaging(app);
    await deleteToken(messaging).catch(() => undefined);
};

export const startForegroundWebPushListener = async (): Promise<
    (() => void) | undefined
> => {
    if (typeof window === 'undefined') {
        return undefined;
    }

    if (!('Notification' in window)) {
        return undefined;
    }

    if (!hasMessagingConfig()) {
        return undefined;
    }

    const supported = await isSupported().catch(() => false);
    if (!supported) {
        return undefined;
    }

    const app = getFirebaseApp();
    if (!app) {
        return undefined;
    }

    if (stopForegroundListener) {
        return stopForegroundListener;
    }

    const messaging = getMessaging(app);

    stopForegroundListener = onMessage(messaging, payload => {
        const messageId = payload.data?.messageId;
        const targetPath = buildWebPushTargetPath(payload.data ?? {});

        notifyForegroundWebPushListeners({
            ...(payload.data ?? {}),
            messageId,
            targetPath,
        });

        if (Notification.permission !== 'granted') {
            return;
        }

        const title = payload.notification?.title || 'Mundu';
        const body = payload.notification?.body || '';

        const notification = new Notification(title, {
            body,
            data: {
                ...(payload.data ?? {}),
                messageId,
                targetPath,
            },
        });

        notification.onclick = () => {
            window.focus();
            window.open(targetPath, '_blank', 'noopener,noreferrer');
            notification.close();
        };
    });

    return stopForegroundListener;
};
